import { defineComponent as _defineComponent } from 'vue'
import {computed, getCurrentInstance, onMounted, Ref, ref} from 'vue';
import {DataTableHeader} from '@/services/searchEngine/interfaces/DataTableHeader';
import {NPSFilter} from '@/models/Filter';
import FpDataTable from '@/components/datatables/FpDataTable.vue';
import TablePagination from '@/components/commons/TablePagination.vue';
import PersonAvatar from '@/components/commons/PersonAvatar.vue';
import {NPSFeedback, NPSOpinion} from '@/modules/nps/models/NPS';
import GoToService from '@/router/GoToService';
import User from '@/models/user/User';
import UserApi from '@/modules/user/services/UserApi';
import NPSApi from '@/modules/nps/services/NPSApi';
import LRating from '@/components/stats/LRating.vue';
import {cloneDeep, isEqual} from 'lodash-es';
import FpFilter from '@/components/inputs/FpFilter.vue';
import BranchTypes from '@/modules/branches/services/BranchTypes';
import {FilterType} from '@/services/searchEngine/enums/FilterType';
import {debouncedAtInput} from '@/services/utils/BasicUtils';
import {FeedbackSourceType} from '@/modules/nps/enums/FeedbackSourceType';

type RatingConfig = {
  model: keyof NPSFeedback,
  label?: string,
  tooltip?: string,
  simple?: boolean,
}

type NPSTableElement = {
  id: number,
  user: number,
  branch: string,
  client: number,
  createdAt: string,
  ratings: Array<number>,
  opinion: string,
  management: any,
  deal: string,
  source: string,
  load: boolean,
  clientPhone: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NPSDataTable',
  props: {
    opinions: null,
    params: null,
    short: { type: Boolean },
    simple: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  opinions?: Array<NPSOpinion>;
  params?: NPSFilter,
  short?: boolean;
  simple?: boolean;
}



const MAX_INITIALS_LENGTH = 3;

const items: Ref<Array<NPSOpinion>> = ref([]);
const users: Ref<Array<User>> = ref([]);
const options = ref<NPSFilter>(new NPSFilter());
const oldOptions: Ref<NPSFilter> = ref(cloneDeep(options.value));
const loading: Ref<boolean> = ref(false);
const instance = getCurrentInstance();

const headersConfig = computed<Array<DataTableHeader<NPSTableElement, NPSFilter>>>(() => {
  return [
    {text: 'ID', value: 'id', valueSort: 'id', condition: !props.short,},
    {
      text: 'Ekspert',
      value: 'user',
      valueSort: 'userIds',
      valueFilter: 'users',
      filterType: FilterType.AUTOCOMPLETE,
      condition: !props.short,
    },
    {text: 'Placówka', value: 'branch', valueSort: 'branch', condition: !props.short, },
    {text: 'Klient', value: 'client', },
    {text: 'Data wystawienia', value: 'createdAt', valueFilter: 'createdAt', filterType: FilterType.DATE, valueSort: 'createdAt' && (!props.short && !props.simple) , },
    {text: 'Oceny', value: 'ratings', valueFilter: 'rating', filterType: FilterType.RANGE, range: {from: 1, to: 5,},},
    {text: 'Opinia', value: 'opinion', },
    {text: 'Zarządzanie', value: 'management', condition: !props.short, },
    {text: 'Sprawa', value: 'deal', },
    {text: 'Żródło', value: 'source', valueSort: 'source', condition: !props.short, },
    {text: 'Lead', value: 'lead', condition: !props.short, },
    {text: 'Numer telefonu klienta', value: 'clientPhone', valueFilter: 'clientPhone', filterType: FilterType.TEXT,},
  ];
});

const ratingsConfig = computed<Array<RatingConfig>>(() => {
  return [
    {
      model: 'substantivePartOfMeeting',
      label: 'Wiedza',
    },
    {
      model: 'expertInvolvement',
      label: 'Zaangażowanie',
    },
    {
      model: 'expertBehavior',
      label: 'Kultura osobista',
    },
  ];
});

const visibleElements = computed<Partial<Record<keyof NPSTableElement, boolean>>>(() => {
  const visibilityConfig: Partial<Record<keyof NPSTableElement, boolean>> = {};
  headersConfig.value.map(item => {
    const key = item.value as keyof NPSTableElement;
    visibilityConfig[key] = item.condition === undefined || item.condition;
  });

  return visibilityConfig;
});

const headers = computed<Array<DataTableHeader<NPSTableElement, NPSFilter>>>(() => {
  return headersConfig.value
    .filter(item => item.condition === undefined || item.condition)
    .map(x => new DataTableHeader<NPSTableElement, NPSFilter>(x));
});

onMounted(() => {
  if (props.params) {
    options.value = props.params;
    cloneOptions();
  }

  if (props.opinions) {
    items.value = props.opinions;
    fetchUsers();
  } else {
    fetchData();
  }
});

const onUpdateOptions = (newValue: NPSFilter) => {
  if (isEqual(cloneDeep(oldOptions.value), cloneDeep(options.value))) {
    return;
  }

  if (!props.simple && options.value.page) {
    GoToService.setQuery({page: String(options.value.page), }, false);
  }

  cloneOptions();
  fetchData();
};

const filterTextOpinion = async() => {
  if (!options.value.hasTextOpinion) delete options.value.hasTextOpinion;
  await fetchData();
};

const fetchData = async() => {
  loading.value = true;
  try {
    options.value.page = instance?.proxy.$route.query.page ? Number(instance?.proxy.$route.query.page) : 1;
    const response = await NPSApi.getOpinions(options.value);
    items.value = response.items;
    options.value.quantity = response.quantity;
    options.value.page = response.page || options.value.page;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }

  cloneOptions();
  fetchUsers();
};

const getUsersIdsToFetch = () => {
  const usersIds: Array<number> = [];
  items.value.map(item => {
    if (!usersData.value[item.survey.userId] && !usersIds.includes(item.survey.userId)) {
      usersIds.push(item.survey.userId);
    }
  });

  return usersIds;
};

const fetchUsers = async() => {
  if (props.short) {
    return;
  }

  const usersIds = getUsersIdsToFetch();
  if (!usersIds.length) {
    return;
  }

  const response = await UserApi.usersForFilters({users: usersIds,});
  if (response) {
    users.value.push(...response);
  }
};

const usersData = computed<Record<number, User>>(() => {
  const usersMap: Record<number, User> = {};
  users.value.map(user => {
    usersMap[user.id] = user;
  });

  return usersMap;
});

const clientsInitials = computed<Record<number, string>>(() => {
  const initials: Record<number, string> = {};

  items.value.map(item => {
    initials[item.survey.clientId] = (item.survey.clientName.split(' ').map(x => x.substring(0, 1).toUpperCase()).join('') || '').substring(0,MAX_INITIALS_LENGTH);
  });

  return initials;
});

const goToUserDetails = (userId: number) => {
  if (userId) {
    GoToService.user(userId);
  }
};

const goToClientDetails = (clientId: number) => {
  if (clientId) {
    GoToService.client(clientId);
  }
};

const goToDeal = (dealId: number) => {
  if (dealId) {
    GoToService.deal(dealId);
  }
};

const goToBranch = (branchId: number) => {
  if (branchId) {
    GoToService.branch(branchId);
  }
};

const onPublishChange = (item: NPSOpinion) => {
  item.feedback!.calculate = item.feedback!.publish;
  processOpinion(item, item.feedback!.publish, item.feedback!.calculate);
};

const onCalculateChange = (item: NPSOpinion) => {
  processOpinion(item, item.feedback!.publish, item.feedback!.calculate);
};

const processOpinion = async(item: NPSOpinion, publish: boolean = false, calculate: boolean = false) => {
  try {
    await NPSApi.pocessOpinion(item.feedback!.id, { publish, calculate, });
    instance?.proxy.$snackbarService.openSuccessSnackbar('Zmieniono status opinii');
  } catch (e) {
    instance?.proxy.$snackbarService.openErrorSnackbar('Wystąpił bład podczas przetwarzania opinii');
  }
};

const cloneOptions = () => {
  oldOptions.value = cloneDeep(options.value);
};

return { __sfc: true,props, MAX_INITIALS_LENGTH, items, users, options, oldOptions, loading, instance, headersConfig, ratingsConfig, visibleElements, headers, onUpdateOptions, filterTextOpinion, fetchData, getUsersIdsToFetch, fetchUsers, usersData, clientsInitials, goToUserDetails, goToClientDetails, goToDeal, goToBranch, onPublishChange, onCalculateChange, processOpinion, cloneOptions, FpDataTable, TablePagination, PersonAvatar, LRating, FpFilter, BranchTypes, FeedbackSourceType }
}

})